import { Row, SkeletonUnit } from '@everlywell/leaves';
import { Tag, theme, Text, Box } from '@everlywell/ui-kit';
import AddToCartButton from 'components/AddToCartButton';
import ProductTag from 'components/PageSections/components/ProductTag';
import PriceV2 from 'components/PricingV2/PriceV2';
import PromoDiscountPrice from 'components/PricingV2/PromoDiscountPrice';
import ProductCardV2 from 'components/ProductCardV2';
import { GatsbyImage } from 'gatsby-plugin-image';
import React, {
  MouseEventHandler,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  Promo,
  promoAppliesToProduct,
  Tier,
} from 'utils/helpers/activePromoHelpers';
import { isQuestProduct, QUEST_TAG } from 'utils/helpers/products';
import { useHydrateProductsWithScreenerData } from 'utils/hooks/useScreenerData';
import { UserContext } from 'utils/hooks/useUserContext/context';
import { ProductDataType } from 'utils/types';

import { filterPromosForMemberships } from '../../utils/helpers/membershipHelpers';
import PrePurchaseWrapper from '../PrePurchaseWrapper';
import {
  trackAddToCartButtonClick,
  trackCardClick,
} from '../ProductCardWithScreener/productCardHelpers';
import { ProductGridColProps } from '../ProductDisplayGrid/styles';
import { PromoCardV2, PromoProps } from '../ProductDisplayGridV2/PromoCardV2';
import * as S from '../ProductDisplayGridV2/styles';

export type ProductDisplayGridV2Props = {
  products: ProductDataType[];
  columnConfig: ProductGridColProps;
  visiblePromos?: PromoProps[];
  activePromo?: Promo;
  cardTrackingLabel?: string;
  addToCartTrackingLabel?: string;
};

const ProductDisplayGridV2: React.FC<ProductDisplayGridV2Props> = ({
  products,
  columnConfig: { tabletVerticalUpColumns, tabletHorizontalUpColumns },
  visiblePromos,
  activePromo,
  cardTrackingLabel,
  addToCartTrackingLabel,
}) => {
  const { userData } = useContext(UserContext);
  const [promos, setPromos] = useState<PromoProps[] | null>(null);

  useEffect(() => {
    if (visiblePromos && visiblePromos.length > 0) {
      setPromos(
        visiblePromos.filter((promo) =>
          filterPromosForMemberships(
            promo.active,
            promo.slug,
            userData.membershipState,
          ),
        ),
      );
    }
  }, [userData.membershipState, visiblePromos]);

  const productsWithScreenerData = useHydrateProductsWithScreenerData(
    products ?? [],
  );

  const handleAddToCart = (product: ProductDataType) => {
    trackAddToCartButtonClick(
      product.slug,
      addToCartTrackingLabel ||
        'PIPV2 - Product Card - Add To Cart Button Click',
    );
  };

  const handleCardClick =
    (product: ProductDataType): MouseEventHandler<HTMLAnchorElement> =>
    () => {
      trackCardClick(
        product.slug,
        `PIPV2 - ${cardTrackingLabel}` || 'PIPV2 - Product Card Click',
      );
    };

  const getPricingOverride = (product: ProductDataType): React.ReactNode => (
    <PromoDiscountPrice
      price={product.price}
      promo={activePromo}
      productId={product.productId}
      promotionable={product.promotionable}
      memberPrice={product.memberPrice}
      subscribeSelected={false}
      memberHeroCopy={product.memberHeroCopy}
      isTextReduced={true}
      isOutOfStock={product.outOfStock}
      promoText={activePromo ? undefined : product.promoText}
    />
  );

  return (
    <div
      role="region"
      id="productGrid"
      data-testid="productGrid"
      aria-live="polite"
    >
      <Row>
        <S.ProductGridCol
          xs={12}
          tabletVerticalUpColumns={tabletVerticalUpColumns}
          tabletHorizontalUpColumns={tabletHorizontalUpColumns}
          useShadow
        >
          {!productsWithScreenerData || !productsWithScreenerData.length ? (
            // Show 6 skeleton loaders while the products are loading
            [...Array(6)].fill(null).map((_, index) => (
              <S.SkeletonLoader
                key={index}
                height={{
                  value: 100,
                  unit: SkeletonUnit.Pixel,
                }}
                width={{
                  value: 100,
                  unit: SkeletonUnit.Percentage,
                }}
              />
            ))
          ) : (
            <>
              {promos?.map((promo, index) => (
                <PromoCardV2 key={index} {...promo} />
              ))}
              {productsWithScreenerData.map(
                (product: ProductDataType, index: number) => {
                  if (!product || product.price === 0) return null;

                  const productHasPromo =
                    product?.productId &&
                    activePromo &&
                    promoAppliesToProduct(
                      product.productId,
                      activePromo,
                      product.promotionable,
                    );

                  return (
                    <ProductCardV2
                      handleOnClick={handleCardClick(product)}
                      key={product.slug}
                      tag={
                        <Box
                          display={['none', 'flex']}
                          gap="0.5"
                          alignItems="center"
                        >
                          <CardTags
                            product={product}
                            activePromoForProduct={
                              productHasPromo ? activePromo : undefined
                            }
                          />
                        </Box>
                      }
                      variant="default"
                      image={
                        <>
                          <Box display={['block', 'none']} height="100%">
                            {product?.boxImage?.gatsbyImageData && (
                              <GatsbyImage
                                image={product?.boxImage?.gatsbyImageData}
                                alt=""
                                imgStyle={{ objectFit: 'contain' }}
                                style={{ height: '100%' }}
                              />
                            )}
                          </Box>
                          <Box display={['none', 'block']} height="100%">
                            {product?.productCardImage?.gatsbyImageData && (
                              <GatsbyImage
                                image={
                                  product?.productCardImage?.gatsbyImageData
                                }
                                alt=""
                                imgStyle={{ objectFit: 'contain' }}
                                style={{ height: '100%' }}
                              />
                            )}
                          </Box>
                        </>
                      }
                      pricing={
                        <>
                          <Text
                            fontSize="lg"
                            color={theme.colors.tints.black}
                            fontWeight={'medium'}
                          />
                          <PriceV2 dollarAmount={product.price} />
                        </>
                      }
                      pricingOverride={getPricingOverride(product)}
                      cta={
                        <AddToCartButton
                          product={product}
                          clickAnalyticsData={{
                            label: 'PIPV2 - Add to cart',
                          }}
                          onClick={() => handleAddToCart(product)}
                        />
                      }
                      linkTo={product.slug}
                    >
                      <Box
                        display={['flex', 'none']}
                        marginBottom={2}
                        gap="0.5"
                        alignItems="center"
                      >
                        <CardTags
                          product={product}
                          activePromoForProduct={
                            productHasPromo ? activePromo : undefined
                          }
                        />
                      </Box>
                      <Text
                        fontSize={['lg', '3xl']}
                        color="black"
                        fontWeight="medium"
                        marginY="2"
                        textAlign={['left', 'center']}
                      >
                        {product.displayName}
                      </Text>
                      <Text
                        fontSize="sm"
                        color={theme.colors.tints.darkGrey}
                        textAlign={['left', 'center']}
                      >
                        {product.shortDescription}
                      </Text>
                    </ProductCardV2>
                  );
                },
              )}
            </>
          )}
        </S.ProductGridCol>
      </Row>
      <PrePurchaseWrapper />
    </div>
  );
};

export default ProductDisplayGridV2;

/**
 * Render the tags of a product based on its attributes
 */
export type CardTagsProps = {
  product: ProductDataType;
  activePromoForProduct?: Promo;
};

const getDiscountText = (promo: Promo, productPrice: number): string => {
  if (promo.discounts.flat_percent) {
    return `Save ${promo.discounts.flat_percent}%`;
  }

  // Get the best tiered discount
  const tiers: Tier[] | null = promo.discounts.tiered;
  if (tiers && tiers.length > 0) {
    const bestTier = tiers.reduce((bestTier, tier) => {
      const [minPrice, maxPrice] = tier.price_range;
      if (
        productPrice >= minPrice &&
        (maxPrice === null || productPrice <= maxPrice) &&
        (!bestTier || tier.percent > bestTier.percent)
      ) {
        return tier;
      }
      return bestTier;
    }, null as Tier | null);

    if (bestTier) {
      return `Save ${bestTier.percent}%`;
    }
  }

  return '';
};

export const CardTags = ({ product, activePromoForProduct }: CardTagsProps) => {
  let discountText = null;

  if (activePromoForProduct) {
    discountText = getDiscountText(activePromoForProduct, product.price);
  }

  return (
    <>
      {product?.onSale && (
        <Tag variant="callout" size="sm">
          Sale
        </Tag>
      )}
      {discountText && !product?.outOfStock ? (
        <Tag variant="callout" size="sm">
          {discountText}
        </Tag>
      ) : null}
      {product?.isNew && (
        <Tag variant="callout" size="sm">
          New!
        </Tag>
      )}
      {isQuestProduct(product?.slug) && <ProductTag displayText={QUEST_TAG} />}
      {product?.outOfStock && (
        <Tag variant="disabled" size="sm">
          Out of stock
        </Tag>
      )}
    </>
  );
};
